import { useEffect, useState } from "react";
import axios from "axios";
import { Check, Mail, Gift, Sparkles, Lock } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Reusable email-gated lead magnet.
 *
 * Props:
 *   storageKey      - unique localStorage key per page (e.g. "boundary-builder-unlocked")
 *   source          - tag sent to ConvertKit for segmentation (e.g. "boundary-builder-lead-magnet")
 *   formId          - optional ConvertKit Form ID override for this page
 *   title           - headline shown in the gate card
 *   pitch           - pitch copy shown under the headline
 *   bullets         - string[] of 3 value-prop bullets
 *   ctaLabel        - button label (e.g. "✨ Unlock 3 Free Scripts")
 *   unlockedTitle   - green banner headline after submit
 *   unlockedCopy    - green banner body after submit
 *   theme           - "teal" | "purple" | "teal-purple" (gradient variant)
 *
 * Exposes:
 *   onUnlock(fn)    - optional callback fired after successful unlock
 *
 * Returns a render-prop-free block. Use the hook `useLeadMagnetState(storageKey)`
 * in the parent page to read the `unlocked` boolean and gate content (blur/lock).
 */

export const useLeadMagnetState = (storageKey) => {
  const [unlocked, setUnlocked] = useState(false);

  useEffect(() => {
    try {
      if (localStorage.getItem(storageKey) === "true") {
        setUnlocked(true);
      }
    } catch (_) {}
  }, [storageKey]);

  const markUnlocked = () => {
    try {
      localStorage.setItem(storageKey, "true");
    } catch (_) {}
    setUnlocked(true);
  };

  return { unlocked, markUnlocked };
};

const themeGradients = {
  teal: "from-[#0F766E] via-[#134E4A] to-[#042F2E]",
  purple: "from-purple-600 via-purple-700 to-purple-800",
  "teal-purple": "from-purple-600 via-purple-700 to-teal-600",
};

const themeButton = {
  teal: "from-[#0F766E] to-[#134E4A] hover:from-[#134E4A] hover:to-[#042F2E]",
  purple: "from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800",
  "teal-purple": "from-purple-600 to-teal-600 hover:from-purple-700 hover:to-teal-700",
};

export const LeadMagnet = ({
  storageKey,
  source,
  formId = null,
  title = "Unlock the Rest — Free",
  pitch = "Enter your email to unlock the full guide right now, plus weekly tips from Temple.",
  bullets = [
    "Unlock the full guide on this page",
    "Weekly tips from Temple",
    "Unsubscribe anytime — no spam, ever",
  ],
  ctaLabel = "✨ Unlock the Full Guide",
  unlockedTitle = "All content unlocked",
  unlockedCopy = "Welcome to the Mind Body Spirit Hygiene Tools community. Watch your inbox for Temple's first email.",
  theme = "teal",
  unlocked,
  markUnlocked,
  anchorId = "lead-magnet",
}) => {
  const [formData, setFormData] = useState({ firstName: "", email: "" });
  const [status, setStatus] = useState({ type: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: "", message: "" });

    try {
      await axios.post(`${API}/subscribe`, {
        firstName: formData.firstName,
        email: formData.email,
        form_id: formId || undefined,
        source,
      });
      markUnlocked();
      setStatus({
        type: "success",
        message: "🎉 Unlocked! Scroll down — the full guide is now yours.",
      });
    } catch (err) {
      setStatus({
        type: "error",
        message: "Something went wrong. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (unlocked) {
    return (
      <div
        id={anchorId}
        className="print:hidden mb-12 bg-gradient-to-br from-green-50 to-teal-50 border-2 border-green-300 rounded-2xl p-6 text-center"
        data-testid="lead-magnet-unlocked"
      >
        <div className="inline-flex items-center gap-2 text-green-700 font-bold text-sm mb-1">
          <Check className="w-5 h-5" /> {unlockedTitle}
        </div>
        <p className="text-sm text-gray-700">{unlockedCopy}</p>
      </div>
    );
  }

  const grad = themeGradients[theme] || themeGradients.teal;
  const btn = themeButton[theme] || themeButton.teal;

  return (
    <div
      id={anchorId}
      className="print:hidden mb-12 relative overflow-hidden"
      data-testid="lead-magnet-gate"
    >
      <div
        className={`bg-gradient-to-br ${grad} rounded-3xl p-6 md:p-10 shadow-2xl relative`}
      >
        <div className="absolute top-4 right-4 opacity-20">
          <Sparkles className="w-20 h-20 text-yellow-300" />
        </div>

        <div className="grid md:grid-cols-2 gap-8 items-center relative z-10">
          <div>
            <div className="inline-flex items-center gap-2 bg-yellow-300/90 text-gray-900 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-widest mb-3">
              <Gift className="w-3.5 h-3.5" />
              Free — No Purchase Needed
            </div>
            <h3 className="font-serif text-2xl md:text-3xl text-white leading-tight mb-3">
              {title}
            </h3>
            <p className="text-white/85 text-sm md:text-base leading-relaxed mb-4">
              {pitch}
            </p>
            <ul className="space-y-1.5 mb-2">
              {bullets.map((b, i) => (
                <li
                  key={i}
                  className="flex items-center gap-2 text-white/80 text-sm"
                >
                  <Check className="w-4 h-4 text-yellow-300 shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>

          <form
            onSubmit={handleSubmit}
            className="bg-white rounded-2xl p-5 md:p-6 shadow-xl space-y-3"
            data-testid="lead-magnet-form"
          >
            <div className="relative">
              <input
                type="text"
                placeholder="First name"
                value={formData.firstName}
                onChange={(e) =>
                  setFormData({ ...formData, firstName: e.target.value })
                }
                required
                data-testid="lead-magnet-firstname"
                className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50/50 text-[#042F2E] placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-sm"
              />
            </div>
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <input
                type="email"
                placeholder="Your email"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                required
                data-testid="lead-magnet-email"
                className="w-full pl-11 pr-4 py-3 rounded-xl border border-gray-200 bg-gray-50/50 text-[#042F2E] placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-sm"
              />
            </div>
            <button
              type="submit"
              disabled={isSubmitting}
              data-testid="lead-magnet-submit"
              className={`w-full bg-gradient-to-r ${btn} text-white rounded-xl py-3 font-bold text-sm transition-all hover:-translate-y-0.5 shadow-md disabled:opacity-50 disabled:hover:translate-y-0`}
            >
              {isSubmitting ? "Unlocking…" : ctaLabel}
            </button>
            {status.message && (
              <p
                className={`text-xs text-center ${
                  status.type === "error" ? "text-red-600" : "text-green-700"
                }`}
                data-testid="lead-magnet-status"
              >
                {status.message}
              </p>
            )}
            <p className="text-[11px] text-gray-400 text-center">
              We'll never share your email. Unsubscribe anytime.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

/**
 * Reusable blur-lock overlay. Wrap any content with <LockOverlay locked={bool}>...</LockOverlay>
 * or use the simpler standalone <LockedBadge /> to render the overlay on top of existing content.
 */
export const LockedBadge = ({ anchor = "#lead-magnet", label = "Unlock all free" }) => (
  <div className="absolute inset-0 flex items-center justify-center bg-white/60 backdrop-blur-[2px] print:hidden z-10">
    <div className="text-center p-4">
      <div className="w-12 h-12 rounded-full bg-[#0F766E] flex items-center justify-center mx-auto mb-2 shadow-lg">
        <Lock className="w-5 h-5 text-white" />
      </div>
      <p className="text-xs font-bold text-[#042F2E] mb-0.5">Locked — {label}</p>
      <a href={anchor} className="text-[11px] text-teal-700 font-semibold underline">
        Enter email above ↑
      </a>
    </div>
  </div>
);
